import { FormEvent, useEffect, useState } from "react";
import QRCode from "qrcode";
import { Field } from "../../ui/Field";
import { TextInput } from "../../ui/TextInput";
import { Button } from "../../ui/Button";
import { Banner } from "../../ui/Banner";
import { Spinner } from "../../ui/Spinner";
import { pickFieldError } from "../../lib/form";
import { useTranslation, apiErrorMessage } from "../../i18n";
import { ApiError } from "../../api/client";
import {
  useTotpStatus,
  useTotpEnroll,
  useTotpConfirm,
  useTotpDisable,
  useTotpRegenerateCodes,
  usePasskeys,
  useRegisterPasskey,
  useDeletePasskey,
  type TotpEnrollment,
} from "./useSecurity";

function Card({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
      <h2 className="mb-4 text-lg font-semibold text-slate-100">{title}</h2>
      {children}
    </section>
  );
}

export function SecuritySection() {
  const { t } = useTranslation("account");
  return (
    <>
      <Card title={t("totp.title")}>
        <TotpCard />
      </Card>
      <Card title={t("passkeys.title")}>
        <PasskeyCard />
      </Card>
    </>
  );
}

// ── TOTP ────────────────────────────────────────────────────────────────────

function TotpCard() {
  const { t } = useTranslation("account");
  const status = useTotpStatus();
  // Recovery codes are shown ONCE and are unrecoverable afterwards, so they
  // live ABOVE the enabled/setup switch: issuing them invalidates the status
  // query, which flips this card and would otherwise unmount the panel holding
  // them before the user could copy any down. Why they are on screen is carried
  // WITH them — inferring it from the status query races that same
  // invalidation, and a fresh enrollment then claims to have replaced codes the
  // user never had.
  const [issued, setIssued] = useState<
    { codes: string[]; reason: "enrolled" | "reissued" } | null
  >(null);

  if (issued) {
    return (
      <RecoveryCodePanel
        codes={issued.codes}
        reason={issued.reason}
        onDismiss={() => setIssued(null)}
      />
    );
  }
  if (status.isLoading) {
    return <div className="flex justify-center p-4"><Spinner /></div>;
  }
  // Never fall through to the setup flow on an INDETERMINATE status. Enrolling
  // overwrites a confirmed row and deletes every recovery code, so offering it
  // to a user who may already have the factor turns a transient outage into a
  // silently stripped second factor.
  if (status.isError || !status.data) {
    return (
      <>
        <Banner kind="error" className="mb-4">{t("totp.statusUnavailable")}</Banner>
        <Button type="button" variant="secondary" onClick={() => void status.refetch()}>
          {t("common.retry")}
        </Button>
      </>
    );
  }
  if (status.data.enabled) {
    return (
      <TotpEnabled
        remaining={status.data.recoveryCodesRemaining}
        onReissued={(codes) => setIssued({ codes, reason: "reissued" })}
      />
    );
  }
  return <TotpSetup onConfirmed={(codes) => setIssued({ codes, reason: "enrolled" })} />;
}

/**
 * The one and only display of a set of recovery codes. Dismissing destroys the
 * only plaintext copy that will ever exist, so it is gated on an explicit
 * acknowledgement rather than a bare button — a stray click here costs the user
 * their account-recovery path.
 */
function RecoveryCodePanel({
  codes,
  reason,
  onDismiss,
}: {
  readonly codes: string[];
  readonly reason: "enrolled" | "reissued";
  readonly onDismiss: () => void;
}) {
  const { t } = useTranslation("account");
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <>
      <Banner kind="success" className="mb-4">
        {reason === "enrolled" ? t("totp.setupComplete") : t("totp.recoveryReissued")}
      </Banner>
      <p className="mb-2 text-sm text-slate-400">{t("totp.recoveryIntro")}</p>
      <RecoveryCodes codes={codes} />
      <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" className="h-4 w-4 rounded border-slate-600 bg-slate-800"
          checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
        {t("totp.recoveryAck")}
      </label>
      <Button type="button" variant="secondary" className="mt-3"
        disabled={!acknowledged} onClick={onDismiss}>
        {t("totp.recoveryDone")}
      </Button>
    </>
  );
}

/** One code-entry form serving both re-verified actions on an enabled factor
 *  (disable, reissue recovery codes) — identical shape, different verb. */
function CodeConfirmForm({
  error,
  pending,
  submitLabel,
  submitVariant,
  onSubmit,
  onCancel,
}: {
  readonly error?: string;
  readonly pending: boolean;
  readonly submitLabel: string;
  readonly submitVariant: "danger" | "primary";
  readonly onSubmit: (code: string) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation("account");
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(code); }} className="space-y-3" noValidate>
      {error ? <Banner kind="error">{error}</Banner> : null}
      <Field label={t("totp.codeLabel")}>
        {({ id, ...aria }) => (
          <TextInput id={id} autoComplete="one-time-code" required value={code}
            onChange={(e) => setCode(e.target.value)} {...aria} />
        )}
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant={submitVariant} disabled={pending}>{submitLabel}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button>
      </div>
    </form>
  );
}

function TotpEnabled({
  remaining,
  onReissued,
}: {
  readonly remaining: number;
  readonly onReissued: (codes: string[]) => void;
}) {
  const { t } = useTranslation("account");
  const { t: tErr } = useTranslation("errors");
  const [action, setAction] = useState<"none" | "disable" | "reissue">("none");
  const disable = useTotpDisable();
  const reissue = useTotpRegenerateCodes();

  const errorFor = (e: ApiError | null) =>
    e && e.code !== "validation_failed" ? apiErrorMessage(e, tErr) : pickFieldError(e, "code");

  // The forms are remounted per action (clearing the typed code), but the
  // mutation errors live here — reset them too, or reopening a form greets the
  // user with "Incorrect code" before they have typed anything.
  function close(): void {
    disable.reset();
    reissue.reset();
    setAction("none");
  }

  return (
    <>
      <Banner kind="success" className="mb-4">
        {t("totp.enabled", { count: remaining })}
      </Banner>
      {action === "disable" ? (
        <CodeConfirmForm
          error={errorFor(disable.error)}
          pending={disable.isPending}
          submitLabel={t("totp.confirmDisable")}
          submitVariant="danger"
          onCancel={close}
          onSubmit={(code) => disable.mutate({ code }, { onSuccess: close })}
        />
      ) : action === "reissue" ? (
        <CodeConfirmForm
          error={errorFor(reissue.error)}
          pending={reissue.isPending}
          submitLabel={t("totp.recoveryConfirmRegenerate")}
          submitVariant="primary"
          onCancel={close}
          onSubmit={(code) =>
            reissue.mutate({ code }, {
              onSuccess: (r) => { close(); onReissued(r.recoveryCodes); },
            })
          }
        />
      ) : (
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={() => setAction("reissue")}>
            {t("totp.recoveryRegenerate")}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setAction("disable")}>
            {t("totp.disable")}
          </Button>
        </div>
      )}
    </>
  );
}

function TotpSetup({ onConfirmed }: { readonly onConfirmed: (codes: string[]) => void }) {
  const { t } = useTranslation("account");
  const { t: tErr } = useTranslation("errors");
  const enroll = useTotpEnroll();
  const confirm = useTotpConfirm();
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");

  function onConfirm(e: FormEvent): void {
    e.preventDefault();
    confirm.mutate({ code }, { onSuccess: (r) => onConfirmed(r.recoveryCodes) });
  }

  if (!enrollment) {
    // `unavailable` = the server has no STUDIO_SECRET_ENCRYPTION_KEY, so the
    // secret can't be stored. Same condition, same code, on every TOTP route.
    const err =
      enroll.error?.code === "unavailable" ? t("totp.unavailable") :
      enroll.error ? apiErrorMessage(enroll.error, tErr) : undefined;
    return (
      <>
        <p className="mb-4 text-sm text-slate-400">{t("totp.description")}</p>
        {err ? <Banner kind="error" className="mb-4">{err}</Banner> : null}
        <Button type="button" variant="primary" disabled={enroll.isPending}
          onClick={() => enroll.mutate(undefined, { onSuccess: setEnrollment })}>
          {enroll.isPending ? t("totp.starting") : t("totp.setup")}
        </Button>
      </>
    );
  }

  const codeError =
    confirm.error && confirm.error.code !== "validation_failed"
      ? apiErrorMessage(confirm.error, tErr)
      : pickFieldError(confirm.error, "code");

  return (
    <>
      <TotpQr uri={enrollment.otpauthUri} />
      <p className="mb-1 mt-3 text-sm text-slate-400">{t("totp.manualEntry")}</p>
      <code className="mb-4 block break-all rounded bg-slate-950 px-2 py-1 text-sm text-slate-200">
        {enrollment.secret}
      </code>
      <form onSubmit={onConfirm} className="space-y-3" noValidate>
        <Field label={t("totp.codeLabel")} error={codeError}>
          {({ id, ...aria }) => (
            <TextInput id={id} autoComplete="one-time-code" autoFocus required value={code}
              onChange={(e) => setCode(e.target.value)} hasError={!!codeError} {...aria} />
          )}
        </Field>
        <Button type="submit" variant="primary" disabled={confirm.isPending}>
          {confirm.isPending ? t("totp.verifying") : t("totp.verify")}
        </Button>
      </form>
    </>
  );
}

function TotpQr({ uri }: { readonly uri: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(uri, { margin: 1, width: 192 }).then(
      (url) => alive && setDataUrl(url),
      () => alive && setDataUrl(null),
    );
    return () => {
      alive = false;
    };
  }, [uri]);
  if (!dataUrl) return <div className="flex h-48 w-48 items-center justify-center"><Spinner /></div>;
  return <img src={dataUrl} alt="TOTP QR code" width={192} height={192} className="rounded bg-white p-2" />;
}

function RecoveryCodes({ codes }: { readonly codes: string[] }) {
  return (
    <ul className="grid grid-cols-2 gap-1 rounded bg-slate-950 p-3 font-mono text-sm text-slate-200">
      {codes.map((c) => (
        <li key={c}>{c}</li>
      ))}
    </ul>
  );
}

// ── Passkeys ──────────────────────────────────────────────────────────────

function PasskeyCard() {
  const { t } = useTranslation("account");
  const { t: tErr } = useTranslation("errors");
  const passkeys = usePasskeys();
  const register = useRegisterPasskey();
  const del = useDeletePasskey();
  const [deviceName, setDeviceName] = useState("");

  function onAdd(e: FormEvent): void {
    e.preventDefault();
    register.mutate({ deviceName: deviceName.trim() || undefined }, { onSuccess: () => setDeviceName("") });
  }

  const registerError = register.error
    ? register.error instanceof ApiError
      ? apiErrorMessage(register.error, tErr)
      : t("passkeys.failed")
    : undefined;

  return (
    <>
      <p className="mb-4 text-sm text-slate-400">{t("passkeys.description")}</p>

      {registerError ? <Banner kind="error" className="mb-4">{registerError}</Banner> : null}

      {passkeys.isLoading ? (
        <div className="flex justify-center p-4"><Spinner /></div>
      ) : passkeys.data && passkeys.data.length > 0 ? (
        <ul className="mb-4 divide-y divide-slate-800">
          {passkeys.data.map((pk) => (
            <li key={pk.id} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-200">
                {pk.deviceName || t("passkeys.unnamed")}
              </span>
              <Button type="button" variant="ghost" size="sm" disabled={del.isPending}
                onClick={() => del.mutate({ id: pk.id })}>
                {t("passkeys.remove")}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-slate-500">{t("passkeys.none")}</p>
      )}

      <form onSubmit={onAdd} className="flex items-end gap-2" noValidate>
        <div className="flex-1">
          <Field label={t("passkeys.nameLabel")}>
            {({ id, ...aria }) => (
              <TextInput id={id} type="text" placeholder={t("passkeys.namePlaceholder")}
                value={deviceName} onChange={(e) => setDeviceName(e.target.value)} {...aria} />
            )}
          </Field>
        </div>
        <Button type="submit" variant="primary" disabled={register.isPending}>
          {register.isPending ? t("passkeys.adding") : t("passkeys.add")}
        </Button>
      </form>
    </>
  );
}
